import React, {useState, useContext} from "react";
import {useQueryClient} from "react-query";
import __ from "lodash";
import moment from "moment";
import axios from "axios";
import {Stack} from "@mui/material";
import {IconButton, Tooltip} from '@material-ui/core';
import {HighlightOff, Search} from '@material-ui/icons';

import SystemContext from "../../../SystemContext";
import {ActionsList, AwbNumber, FlightNumber, FormSelect, LightTooltip, MaskedText, Text} from "../../../controls";
import {useTripSearch} from "../../../services/trips";
import { getApiSitePrefix } from "../../../functions/SiteDefaults";
import DateSelector from "../../../controls/DatePicker";
import AirportLookup from "../../Airports/components/AirportsLookup";
import CompanyUuidLookup from "../../Companies/components/CompanyUuidLookup";
import useTripDepartmentUsers from "../../../services/auth/useTripDepartmentUsers";

const apiSitePrefix = getApiSitePrefix();
const dateFormat = "DD/MM/YYYY";
const marginLeft = 10;
const defaultEditProps = {
  style:{fontSize: "0.875rem"}
};
const formatChars={
  "A": "[A-Za-z0-9]",
  "9": "[0-9]",
}
const bookingStatuses = [
  {code: "PRE-BOOKING", value: "Pre-Booking"},
  {code: "BOOKED", value: "Booked"},
  {code: "CONFIRMED", value: "Confirmed"},
  {code: "CANCELLED", value: "Cancelled"},
  {code: "DELIVERED", value: "Delivered"},
  {code: "FLIGHT-SUMMARY", value: "Summary"},
  // {code: "IN-TRANSIT", value: "In Transit"},
  {code: "OFFLOADED", value: "Offloaded"},
  // {code: "PRE-ADVICE", value: "Pre-Advice"},
  // {code: "SPOT", value: "Spot Booked"},
  // {code: "WAREHOUSE", value: "Received"},
  // {code: "SLR-LOADABLE", value: "SLR Loadable"},
  // {code: "SLR-NOT-LOADABLE", value: "SLR Not Loadable"} ,
  // {code: "SLR-ACCEPTED", value: "SLR Accepted"},
  // {code: "SLR-REJECTED", value: "SLR Rejected"},
  // {code: "SPOT-SL-REVIEW", value: "Spot Loadability Review"},
  // {code: "SPOT-SL-LOADABLE", value: "Spot Loadable"},
  // {code: "SPOT-SL-NOT-LOADABLE", value: "Spot Not Loadable"},
  // {code: "SPOT-REJECTED", value: "Spot Rejected"},
  // {code: "SPOT-OFFER", value: "Spot Offer"},
  // {code: "SPOT-ACCEPTED", value: "Spot Accepted"}
];
const emptySearch = {
  airlineCode: "",
  flightNo: "",
  tripNumber: "",
  fromFlightDate: null,
  toFlightDate: null,
  destinationAirportCode: "",
  clientUuid: "",
  accountManagerId: "",
  awbPrefix: "",
  awbSerial: "",
  allotment: "",
  bookingStatus: "",
}


const BookingQueueSelector = (props) => {
  const queryClient = useQueryClient();
  const SystemData = useContext(SystemContext);
  const eventBus = SystemData.eventBus;
  const localValues = SystemData.localValues;

  const [search, setSearch] = useState(props.searchCriteria ?? emptySearch);

  const departmentUsers = useTripDepartmentUsers({queryClient, extraKeys: ["COMMERCIAL"]});
  const userOptions = departmentUsers.data?departmentUsers.data.map((userOption, i) => {
    return {code: userOption.user_id, value: `${userOption.firstName} ${userOption.lastName}`};
  }).sort((u1, u2) => {
    if (u1.value === u2.value) return 0;
    if (u1.value < u2.value) return -1;
    return 1;
  }):null;

  const handleSearchChange = () => {
    let searchCriteria = {...search};
    if (__.has (searchCriteria, "tripNumber") && searchCriteria.tripNumber !== "")
      searchCriteria = __.omit(searchCriteria, ["fromFlightDate", "toFlightDate"]);
    if (eventBus) {
      eventBus.trigger('BookingQueue.search', searchCriteria);
    }
  }

  const handleClearSelections = () => {
    setSearch(emptySearch);
    if (eventBus) {
      eventBus.trigger('BookingQueue.search', {});
    }
  }

  const delay = (milliseconds) => new Promise(resolve => {
    setTimeout(resolve, milliseconds);
  });

  const download = async (url, name) => {
    const a = document.createElement('a');
    a.download = name;
    a.href = url;
    a.style.display = 'none';
    document.body.append(a);
    a.click();

    // Chrome requires the timeout
    await delay(100);
    a.remove();
  };

  const downloadReport = async (report) => {
    let url;
    let reportName = "";

    try {
      SystemData.toggleLoadingProgress(true);
      switch (report) {
        case "GHA":
          url = `${apiSitePrefix}/booking/exportGhaSheet/${search.tripNumber}`;
          reportName = "GHA Booking Sheet";
          break;
        case "ACCOUNTING":
          url = `${apiSitePrefix}/booking/exportAccountingSheets/${search.tripNumber}`;
          reportName = "Accounting Sheet";
          break;
        case "BACKUP":
          url = `${apiSitePrefix}/booking/exportInvoiceBackup/${search.tripNumber}`;
          reportName = "Invoice Backup";
          break;
      }

      const response = await axios.get(url, {
        headers: {
          //'Access-Control-Allow-Headers': ['Origin', 'X-Requested-With', 'Content-Type', 'Accept'],
          'Access-Control-Allow-Origin': '*',
        }
      });

      if (response && response.data) {
        if (response.data.status) {
          if (__.has(response.data, 'urls') && Array.isArray(response.data.urls)) {
            for (let i=0; i < response.data.urls.length; i++) {
              await delay(i * 200);
              download(response.data.urls[i], response.data.fileNames[i])
            }
          } else {
            download(response.data.excelSignedUrl, response.data.downloadFileName);
          }
        } else {
          SystemData.openAlert(
            "Download Error",
            `The ${reportName} Excel file could not be downloaded due to server error: ${response.data.error}`
          );
        }
      }
      SystemData.toggleLoadingProgress(false);
    } catch {
      SystemData.toggleLoadingProgress(false);
      SystemData.openAlert(
        "Download Error",
        `The ${reportName} Excel file could not be downloaded due to an unexpected error`
      );
    }
  }

  const extraKeys = [search.tripNumber]
  const tripRecord = useTripSearch({queryClient, extraKeys, useCache: true});

  const actions = [
    {iconName: "assessment", iconProps: {color: "secondary"}, tooltip: "Trip Summary", disabled: tripRecord?.data?.tripUuid===undefined, onClick:()=>{eventBus.trigger('BookingQueueTrips.tripSummaryRequested', {})}},
    {iconName: "flight", iconProps: {color: "secondary"}, tooltip: "GHA Extract", disabled: tripRecord?.data?.tripUuid===undefined, onClick:()=>{downloadReport("GHA")}},
    {iconName: "account_balance", iconProps: {color: "secondary"}, tooltip: "Generate Accounting Extract", disabled: tripRecord?.data?.tripUuid===undefined, onClick:()=>{downloadReport("ACCOUNTING")}},
    {iconName: "receipt", iconProps: {color: "secondary"}, tooltip: "Invoice Backup", disabled: tripRecord?.data?.tripUuid===undefined, onClick:()=>{downloadReport("BACKUP")}}
  ]

  localValues.unset("FlightBooking");

  return (
    <div style={{padding: "10px 20px", textAlign: "left", width: "calc(100% - 20px)"}}>
      <div style={{display: "inline-flex"}}>
        <div style={{width: 80, display: "inherit", marginTop: 1}}>
          <FlightNumber
            label="Flight"
            mask={"AX-999aa"}
            code={search.airlineCode}
            number={search.flightNo}
            inputProps={defaultEditProps}
            onChange={e => {
              const airlineCode = e.code;
              const flightNo = e.number;
              setSearch({...search, airlineCode, flightNo });
            }}
          />
        </div>
        <MaskedText
          label="Trip Number"
          value={search.tripNumber}
          inputLabelProps={{ shrink: true }}
          style={{display: "inline-block", width: 130, marginLeft: marginLeft}}
          mask={"AAA99999999"}
          forceUpperCase={true}
          formatChars={formatChars}
          onChange={ value => {
            setSearch({...search, tripNumber: value });
          }}
        />
        <DateSelector
          label="From Flight Date"
          emptyLabel={""}
          value={search.fromFlightDate}
          style={{marginLeft: marginLeft, width: 140}}
          // minDate={minDate}
          // emptyValue={" "}
          defaultValue={null}
          disableToolbar={true}
          autoOk={true}
          format={dateFormat}
          invalidDateMessage={search.fromFlightDate === "" || search.fromFlightDate === null ? <></> : "Invalid Date"}
          onChange={(value) => {
            const toFlightDate = !search.toFlightDate || moment(search.toFlightDate).isBefore(moment(value)) ? moment(value).endOf("day").format("YYYY-MM-DD") : search.toFlightDate;
            setSearch({...search, fromFlightDate: moment(value).format("YYYY-MM-DD"), toFlightDate});
          }}
        />
        <DateSelector
          label="To Flight Date"
          value={search.toFlightDate ?? undefined}
          style={{marginLeft: marginLeft, width: 140}}
          minDate={search.fromFlightDate ? moment(search.fromFlightDate).endOf("day").format("YYYY-MM-DD") : undefined}
          defaultValue={null}
          disableToolbar={true}
          autoOk={true}
          format={dateFormat}
          minDateMessage={<></>}
          invalidDateMessage={search.toFlightDate === " " ? <></> : "Invalid Date"}
          onChange={(value) => {
            if (moment(value).isBefore(moment(search.fromFlightDate))) {
              SystemData.alert("Date Error", "'To Flight Date' cannot be before 'From Flight Date'");
              setSearch({...search, toFlightDate: search.fromFlightDate});
            } else {
              setSearch({...search, toFlightDate: moment(value).format("YYYY-MM-DD")});
            }
          }}
        />
        <LightTooltip title={search.destinationAirportName ?? ""} >
          <div style={{width: 80, display: "inherit", marginLeft: marginLeft,}}>
            <AirportLookup
              label="Destination"
              inputLabelProps={{ shrink: true }}
              value={search.destinationAirportCode}
              returnType={"object"}
              onChange={(returnObject, props) => {
                if (returnObject && returnObject.found) {
                  setSearch({...search, destinationAirportCode: returnObject.value.toUpperCase(), destinationAirportName: returnObject.data.airportName});
                } else {
                  setSearch({...search, destinationAirportCode: returnObject.value, destinationAirportName: null});
                }
              }}
            />
          </div>
        </LightTooltip>
        <div style={{width: 240, display: "inherit", marginLeft: marginLeft}}>
          <CompanyUuidLookup
            label="Client / GSA"
            value={search.clientUuid}
            companyType={"CLIENT"}
            shrink={true}
            returnType={"object"}
            onChange={(returnObject, props) => {
              if (returnObject.found) {
                const company = returnObject.data
                setSearch({...search, clientUuid: company.companyUuid, companyName: company.companyName});
              } else {
                setSearch({...search, clientUuid: returnObject.value, companyName: null});
              }
            }}
          />
        </div>
        <div style={{width: 150, display: "inherit", marginLeft: marginLeft, marginTop: 1}}>
          <FormSelect
            label="Account Manager"
            value={search.accountManagerId}
            options={userOptions}
            style={{width: 150}}
            onChange={(value, props) => {
              if (value && __.has(value, "target") && __.has(value.target, "value"))
                setSearch({...search, accountManagerId: value.target.value});
            }}
          />
        </div>
        <div style={{width: 120, display: "inherit", marginLeft: marginLeft,}}>
          <AwbNumber
            label="Air Waybill"
            inputLabelProps={{shrink: true}}
            fullWidth
            prefix={search.awbPrefix}
            serial={search.awbSerial}
            //inputProps={defaultEditProps}
            onChange={(value, props) => {
              setSearch({...search, awbPrefix: value.prefix, awbSerial: value.serial});
            }}
          />
        </div>
        <div style={{width: 120, display: "inherit", marginLeft: marginLeft,}}>
          <MaskedText
            label="Allotment"
            inputLabelProps={{shrink: true}}
            value={search.allotment}
            style={{display: "inline-block", width: 130, marginLeft: marginLeft}}
            mask={"A".repeat(30)}
            forceUpperCase={true}
            formatChars={formatChars}
            onChange={ value => {
              setSearch({...search, allotment: value });
            }}
          />
        </div>
        <div style={{width: 100, display: "inherit", marginLeft: marginLeft, marginTop: 1}}>
          <FormSelect
            label="Status"
            value={search.bookingStatus}
            options={bookingStatuses}
            style={{width: 100}}
            onChange={(value, props) => {
              if (value && __.has(value, "target") && __.has(value.target, "value"))
                setSearch({...search, bookingStatus: value.target.value});
            }}
          />
        </div>
        <div style={{display: "inherit", marginLeft: marginLeft}}>
          <Stack direction="row" alignItems="center" spacing={0}>
            <IconButton onClick={()=>{handleSearchChange()}} >
              <Tooltip title={"Click to retrieve bookings"}>
                <Search style={{height: 24, width: 24, }} color="primary"/>
              </Tooltip>
            </IconButton>
            <IconButton onClick={()=>{handleClearSelections()}}>
              <Tooltip title={"Click to clear all selections"}>
                <HighlightOff style={{height: 24, width: 24, }} color="primary"/>
              </Tooltip>
            </IconButton>
          </Stack>
        </div>
      </div>
      <div style={{display: "inline-block"}}></div>
      <div style={{display: "inline-block", float: "right"}}>
        <ActionsList
          actions={actions}
        />
      </div>
    </div>
  )
}

export default BookingQueueSelector;
